import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import { useMediaQuery } from '@material-ui/core'
import { makeStyles, useTheme } from '@material-ui/core/styles'
import {
  CircularProgress,
  Grid,
  MenuItem,
  ListItemIcon,
  Select,
  FormControl,
  Typography
} from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'

import { useLiterals } from '~/components/Literals'
import { getUrlParams } from '~/utils'
import api from '../api'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 1200,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: 'inherit'
  },
  loading: {
    position: 'absolute',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    width: '100%',
    height: '100%',
    zIndex: 1300,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  containerLogo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF',
    width: '100%'
  },
  containerInput: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    backgroundColor: '#EEEBEB',
    padding: 16,
    width: '100%',
    maxWidth: 700
  },
  logo: {
    margin: 16,
    height: 60,
    maxWidth: '100%'
  },
  select: {
    backgroundColor: '#FFF',

    '& > .MuiSelect-root': {
      display: 'flex',
      alignItems: 'center',
      fontSize: '1rem'
    }
  },
  icon: {
    fill: theme.palette.primary.main,
    marginTop: -1,
    fontSize: 28
  },
  productImg: {
    maxWidth: '100%',
    height: 30
  },
  paper: {
    maxHeight: 300,
    ...theme.gofind.scrollBar
  }
}))

function clearStr(value) {
  let str = value

  str = str.replace(/[ÀÁÂÃÄÅ]/g, 'A')
  str = str.replace(/[àáâãäå]/g, 'a')
  str = str.replace(/[ÈÉÊË]/g, 'E')
  str = str.replace(/[èéêë]/g, 'e')

  return str.toLowerCase().replace(/[^a-z0-9]/gi, '')
}

export default function OnlineStoreEmbed({ config, location }) {
  const classes = useStyles()
  const history = useHistory()
  const theme = useTheme()
  const matches = useMediaQuery(theme.breakpoints.down('sm'))
  const { getLiteral } = useLiterals()

  const qs = getUrlParams(location.search)
  const subdomain = clearStr(config.brandName)
  const domain =
    window.location.origin === 'http://localhost:8080'
      ? `http://localhost:3000/productfinder/${subdomain}`
      : window.location.origin

  const [gtin, setGtin] = useState(qs.gtin ?? 'none')
  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])

  const hasFontFace =
    config?.theme?.fontExternal === 'external' ? `&font-face=${config?.theme?.fontExternal}` : ''

  const getProducts = async (cnpj) => {
    try {
      const products = await api.getProducts(cnpj)

      const productsChangedImage = products.map((p) => {
        const changeUrl = p.imageurl
          .replace('https://images.gofind.online/', 'https://images.pertinhodemim.com/')
          .replace(
            'https://s3-sa-east-1.amazonaws.com/gofind.img.repo/',
            'https://images.pertinhodemim.com/'
          )

        return {
          ...p,
          imageurl: changeUrl.includes('?') ? `${changeUrl}&height=300` : `${changeUrl}?height=300`
        }
      })

      setProducts(productsChangedImage)
    } catch (err) {
      console.log('TCL: ~ getProducts ~ err', err)
    }
  }

  const handleChange = (event) => {
    if (location.search) {
      history.replace({ search: `?gtin=${event.target.value}` })
      return
    }

    setLoading(true)
    setGtin(event.target.value)
  }

  useEffect(() => {
    setLoading(true)

    const qs = getUrlParams(location.search)
    setGtin(qs.gtin ?? 'none')
  }, [location.search])

  useEffect(() => {
    getProducts(config.cnpj)
  }, [])

  useEffect(() => {
    ;(() => {
      const root = document.querySelector('#root-online-store')?.parentNode
      const hasBg = root.style.backgroundColor || root.style.background

      if (!hasBg) root.style.backgroundColor = '#EEEBEB'
    })()
  }, [])

  return (
    <div className={classes.root} id="root-online-store">
      <div className={classes.loading} style={{ display: loading ? 'flex' : 'none' }}>
        <CircularProgress color="primary" size={100} />
      </div>

      <Grid container direction="column" alignItems="center" justifyContent="center">
        <Grid item className={classes.containerLogo}>
          <img
            src={config?.imageUrl}
            alt={config?.brandName}
            className={classes.logo}
            loading="lazy"
          />
        </Grid>

        <Grid item className={classes.containerInput}>
          <Typography variant="body1" style={{ marginBottom: 8 }}>
            {getLiteral('1357')}:
          </Typography>
          <FormControl variant="outlined">
            <Select
              className={classes.select}
              value={gtin}
              autoFocus
              onChange={handleChange}
              IconComponent={(props) => <ExpandMoreIcon {...props} />}
              inputProps={{
                classes: {
                  icon: classes.icon
                }
              }}
              MenuProps={{
                classes: {
                  paper: classes.paper
                },
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'center'
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'center'
                },
                getContentAnchorEl: null
              }}
            >
              <MenuItem value="none" disabled>
                <Typography variant="body1" style={{ fontWeight: 500 }}>
                  Selecione...
                </Typography>
              </MenuItem>

              {!products.length && (
                <MenuItem disabled>
                  <Typography
                    variant="body1"
                    style={{ textAlign: 'center', width: '100%', fontSize: 18 }}
                  >
                    Carregando...
                  </Typography>
                </MenuItem>
              )}

              {products.map((p) => (
                <MenuItem key={p.gtin} value={p.gtin}>
                  <ListItemIcon>
                    <img
                      src={p.imageurl}
                      alt={p.name}
                      className={classes.productImg}
                      loading="lazy"
                      onError={({ currentTarget }) => {
                        currentTarget.onerror = null // prevents looping

                        currentTarget.src = `https://s3.sa-east-1.amazonaws.com/gofind.img.repo/productfinder/Grupo+417.svg`
                      }}
                    />
                  </ListItemIcon>
                  <Typography variant="inherit">{p.name}</Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <iframe
        id={`${subdomain}-iframe`}
        src={`${domain}/product/${gtin}?test=true#&online-store${hasFontFace}`}
        allow="geolocation"
        frameBorder="0"
        width="100%"
        style={{ height: '100%' }}
        onLoad={() => {
          setLoading(false)
        }}
      />
    </div>
  )
}
